/**
 * WF-Select - Custom dropdown select component for wizard forms
 *
 * A single-value dropdown with keyboard navigation and ARIA combobox pattern.
 * Uses slotted <option> elements for web-standard option declaration.
 *
 * @example
 * <wf-select name="session" label="Session" placeholder="Select a session" required>
 *   <option value="session-a">Session A</option>
 *   <option value="session-b">Session B</option>
 * </wf-select>
 */

import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { FormInputBase } from './base/form-input-base.js';
import { ValidationController } from '../controllers/ValidationController.js';
import { wfSelectStyles } from './wf-select.styles.js';

interface SelectOption {
  value: string;
  label: string;
}

@customElement('wf-select')
export class WfSelect extends FormInputBase {
  static override styles = wfSelectStyles;

  // ============================================
  // Properties
  // ============================================

  @property({ type: String })
  placeholder = '';

  @property({ type: Boolean })
  disabled = false;

  // ============================================
  // Internal State
  // ============================================

  @state()
  private _open = false;

  @state()
  private _options: SelectOption[] = [];

  @state()
  private _activeIndex = -1;

  @state()
  private _selectedLabel = '';

  private _boundClickOutside = this._handleClickOutside.bind(this);

  // ============================================
  // Lifecycle
  // ============================================

  override connectedCallback(): void {
    super.connectedCallback();
    this._collectOptions();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('click', this._boundClickOutside);
  }

  override firstUpdated(): void {
    this._collectOptions();
  }

  // ============================================
  // Abstract Implementations
  // ============================================

  protected _getValidationTriggerProperties(): string[] {
    return ['required'];
  }

  protected _setupValidators(): void {
    this._validators = [];
    if (this.required) {
      this._validators.push(ValidationController.required());
    }
  }

  protected override _getFocusableSelector(): string {
    return '.wf-select-trigger';
  }

  override reset(): void {
    super.reset();
    this._selectedLabel = '';
    this._open = false;
    this._activeIndex = -1;
  }

  // ============================================
  // Option Collection
  // ============================================

  private _collectOptions(): void {
    const optionEls = this.querySelectorAll('option');
    this._options = Array.from(optionEls).map((opt) => ({
      value: opt.value,
      label: opt.textContent?.trim() || opt.value,
    }));
  }

  // ============================================
  // Open / Close
  // ============================================

  private _toggle(): void {
    if (this.disabled) { return; }
    if (this._open) {
      this._close();
    } else {
      this._openPanel();
    }
  }

  private _openPanel(): void {
    this._open = true;
    // Set active index to current selection or first option
    const selectedIdx = this._options.findIndex((o) => o.value === this._value);
    this._activeIndex = selectedIdx >= 0 ? selectedIdx : 0;
    document.addEventListener('click', this._boundClickOutside);
  }

  private _close(): void {
    this._open = false;
    this._activeIndex = -1;
    document.removeEventListener('click', this._boundClickOutside);
  }

  private _handleClickOutside(e: MouseEvent): void {
    if (!this.contains(e.target as Node) && !this.shadowRoot?.contains(e.target as Node)) {
      this._close();
    }
  }

  // ============================================
  // Selection
  // ============================================

  private _selectOption(option: SelectOption): void {
    this._value = option.value;
    this._selectedLabel = option.label;
    this._close();

    // Debounced validation after activation
    if (this._validationActivated) {
      this.validate();
    }

    // Dispatch change event (same contract as all form inputs)
    this.dispatchEvent(
      new CustomEvent('wf-change', {
        detail: { name: this.name, value: this._value },
        bubbles: true,
        composed: true,
      })
    );
  }

  // ============================================
  // Keyboard Navigation
  // ============================================

  private _handleKeyDown(e: KeyboardEvent): void {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!this._open) {
          this._openPanel();
        } else {
          this._activeIndex = Math.min(this._activeIndex + 1, this._options.length - 1);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (this._open) {
          this._activeIndex = Math.max(this._activeIndex - 1, 0);
        }
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (this._open && this._activeIndex >= 0) {
          this._selectOption(this._options[this._activeIndex]);
        } else if (!this._open) {
          this._openPanel();
        }
        break;
      case 'Escape':
        if (this._open) {
          e.preventDefault();
          e.stopPropagation();
          this._close();
          // Refocus trigger
          (this.shadowRoot?.querySelector('.wf-select-trigger') as HTMLElement)?.focus();
        }
        break;
      case 'Tab':
        if (this._open) {
          this._close();
        }
        break;
    }
  }

  // ============================================
  // Render
  // ============================================

  render() {
    const activeDescendant = this._activeIndex >= 0 ? `wf-select-opt-${this._activeIndex}` : '';

    return html`
      <div class="wf-field-container">
        ${this._renderLabel()}

        <button
          type="button"
          class="wf-select-trigger ${this._errorMessage ? 'wf-select-error' : ''}"
          role="combobox"
          aria-expanded="${this._open}"
          aria-haspopup="listbox"
          aria-activedescendant="${activeDescendant || nothing}"
          aria-controls="wf-select-listbox"
          ?disabled="${this.disabled}"
          data-testid="wf-select-trigger"
          @click="${this._toggle}"
          @keydown="${this._handleKeyDown}"
        >
          ${this._selectedLabel
            ? html`<span>${this._selectedLabel}</span>`
            : html`<span class="wf-select-placeholder">${this.placeholder}</span>`}
          <svg class="wf-select-chevron ${this._open ? 'wf-open' : ''}" viewBox="0 0 16 16" fill="none">
            <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <div
          class="wf-select-panel ${this._open ? 'wf-open' : ''}"
          role="listbox"
          id="wf-select-listbox"
          data-testid="wf-select-panel"
        >
          ${this._options.map(
            (opt, i) => html`
              <div
                class="wf-select-option ${i === this._activeIndex ? 'wf-active' : ''} ${opt.value === this._value ? 'wf-selected' : ''}"
                role="option"
                id="wf-select-opt-${i}"
                aria-selected="${opt.value === this._value}"
                data-testid="wf-select-option"
                @click="${() => this._selectOption(opt)}"
                @mouseenter="${() => { this._activeIndex = i; }}"
              >
                ${opt.label}
              </div>
            `
          )}
        </div>

        <div class="wf-options-slot">
          <slot @slotchange="${this._collectOptions}"></slot>
        </div>

        ${this._renderHint()}
        ${this._renderError()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'wf-select': WfSelect;
  }
}
